/**
 * The announcement editor's block view: one card per Slack block, each with the
 * editor its type deserves and the Slack rendering of that block beside it.
 *
 * It exists because reviewing release notes and editing Block Kit JSON are different
 * jobs. The operator's question is "does this paragraph read right to CS?", and in a
 * JSON textarea that question is buried under quoting, `\n` escapes, and 3000-character
 * lines. Here each paragraph is a text box, emphasis is a button, and the rendered
 * result is next to it — while the file itself stays exactly the artifact the host
 * validates and commits.
 *
 * Every edit goes through the shared `json -> json` transforms, so this component owns
 * no copy of the announcement: the draft string in `AnnouncementEditor` remains the
 * single source of truth, which is what keeps its "unsaved edit" test and the Block Kit
 * validation honest no matter which view made the change.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import LinkIcon from '@mui/icons-material/Link';
import {
    blockKind,
    blockText,
    blockTextLimit,
    blockToJson,
    insertBlock,
    moveBlock,
    parseAnnouncementBlocks,
    parseBlockJson,
    removeBlock,
    setBlockText,
    setRawBlock,
    type AnnouncementBlockKind,
} from '@finvari/deployer';
import { SlackBlocks } from './AnnouncementPreview';
import { MRKDWN_MARKER, bulletLines, insertLink, wrapSelection, type SelectionEdit } from '../lib/mrkdwn';

export interface AnnouncementBlockEditorProps {
    /** The draft `slack_announce.json`; must parse, or the JSON view is the only option. */
    json: string;
    onChange: (json: string) => void;
    disabled?: boolean;
}

/** The block types the "add block" menu offers, in the order the prompts use them. */
const ADDABLE: { kind: AnnouncementBlockKind; label: string }[] = [
    { kind: 'section', label: 'Section' },
    { kind: 'divider', label: 'Divider' },
    { kind: 'header', label: 'Header' },
];

export function AnnouncementBlockEditor({ json, onChange, disabled }: AnnouncementBlockEditorProps) {
    const blocks = useMemo(() => parseAnnouncementBlocks(json).blocks, [json]);

    return (
        <Stack spacing={2}>
            {blocks.length === 0 && (
                <Alert severity="info">
                    This announcement has no blocks. The JSON view shows what it does carry.
                </Alert>
            )}
            {blocks.map((block, index) => (
                <BlockCard
                    key={index}
                    block={block}
                    index={index}
                    count={blocks.length}
                    json={json}
                    onChange={onChange}
                    disabled={disabled}
                />
            ))}
            <Box>
                <AddBlockMenu
                    label="Add block"
                    disabled={disabled}
                    onPick={(added) => onChange(insertBlock(json, blocks.length, added))}
                />
            </Box>
        </Stack>
    );
}

interface BlockProps {
    block: unknown;
    index: number;
    count: number;
    json: string;
    onChange: (json: string) => void;
    disabled?: boolean;
}

/** One block: what it is, where it sits in the message, and how to edit it. */
function BlockCard(props: BlockProps) {
    const { block, index, count, json, onChange, disabled } = props;
    const kind = blockKind(block);
    const type = (block as { type?: string } | null)?.type;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: kind === 'divider' ? 1 : 2 }}>
                <Chip size="small" label={type ?? 'unknown'} />
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Move up">
                    <span>
                        <IconButton
                            size="small"
                            aria-label={`Move block ${index + 1} up`}
                            disabled={disabled || index === 0}
                            onClick={() => onChange(moveBlock(json, index, index - 1))}
                        >
                            <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Move down">
                    <span>
                        <IconButton
                            size="small"
                            aria-label={`Move block ${index + 1} down`}
                            disabled={disabled || index === count - 1}
                            onClick={() => onChange(moveBlock(json, index, index + 1))}
                        >
                            <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <AddBlockMenu
                    iconOnly
                    label="Add block below"
                    disabled={disabled}
                    onPick={(added) => onChange(insertBlock(json, index + 1, added))}
                />
                <Tooltip title="Delete this block">
                    <span>
                        <IconButton
                            size="small"
                            aria-label={`Delete block ${index + 1}`}
                            disabled={disabled}
                            onClick={() => onChange(removeBlock(json, index))}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {kind === 'header' && <HeaderFields {...props} />}
            {kind === 'section' && <SectionFields {...props} />}
            {kind === 'divider' && <Divider />}
            {kind === 'raw' && <RawFields {...props} />}
        </Paper>
    );
}

/** A header is plain text — Slack renders no markup in it, so there is no toolbar. */
function HeaderFields({ block, index, json, onChange, disabled }: BlockProps) {
    const text = blockText(block);
    const limit = blockTextLimit('header');
    return (
        <TextField
            label="Header"
            value={text}
            onChange={(e) => onChange(setBlockText(json, index, e.target.value))}
            fullWidth
            disabled={disabled}
            error={text.length > limit}
            helperText={`${text.length} / ${limit}`}
        />
    );
}

/**
 * A section, the block the release notes are actually written in: the mrkdwn on the
 * left, Slack's rendering of it on the right. The source stays visible rather than
 * being replaced by a WYSIWYG surface because the file is the artifact under review —
 * the operator is approving the bytes that get committed and posted.
 */
function SectionFields({ block, index, json, onChange, disabled }: BlockProps) {
    const text = blockText(block);
    const limit = blockTextLimit('section');
    const inputRef = useRef<HTMLTextAreaElement>(null);

    /**
     * Run a toolbar transform over the field's current selection, then put the caret
     * back where the transform asked. The restore waits a frame because the field is
     * controlled: React has to render the new text before a selection range set on it
     * means anything.
     */
    const apply = (transform: (text: string, start: number, end: number) => SelectionEdit) => {
        const el = inputRef.current;
        if (!el) return;
        const edit = transform(el.value, el.selectionStart ?? 0, el.selectionEnd ?? 0);
        onChange(setBlockText(json, index, edit.text));
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(edit.selectionStart, edit.selectionEnd);
        });
    };

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <SectionToolbar disabled={disabled} inputRef={inputRef} apply={apply} />
                <TextField
                    value={text}
                    onChange={(e) => onChange(setBlockText(json, index, e.target.value))}
                    multiline
                    minRows={6}
                    fullWidth
                    disabled={disabled}
                    inputRef={inputRef}
                    error={text.length > limit}
                    helperText={`${text.length} / ${limit}`}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <SlackBlocks blocks={[block]} />
                </Paper>
            </Grid>
        </Grid>
    );
}

interface SectionToolbarProps {
    disabled?: boolean;
    inputRef: React.RefObject<HTMLTextAreaElement>;
    apply: (transform: (text: string, start: number, end: number) => SelectionEdit) => void;
}

/** The mrkdwn actions. Titles name the markup so the operator learns it in passing. */
function SectionToolbar({ disabled, inputRef, apply }: SectionToolbarProps) {
    const [linkOpen, setLinkOpen] = useState(false);
    // The selection as it was when Link was clicked: opening the dialog moves focus,
    // and the URL the operator then types must wrap the text they had highlighted.
    const selection = useRef({ start: 0, end: 0 });

    const button = (title: string, label: string, icon: React.ReactNode, onClick: () => void) => (
        <Tooltip title={title} key={label}>
            <span>
                <ToggleButton value={label} size="small" aria-label={label} disabled={disabled} onClick={onClick}>
                    {icon}
                </ToggleButton>
            </span>
        </Tooltip>
    );

    return (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
            {button('Bold — *text*', 'Bold', <FormatBoldIcon fontSize="small" />, () =>
                apply((t, s, e) => wrapSelection(t, s, e, MRKDWN_MARKER.bold)),
            )}
            {button('Italic — _text_', 'Italic', <FormatItalicIcon fontSize="small" />, () =>
                apply((t, s, e) => wrapSelection(t, s, e, MRKDWN_MARKER.italic)),
            )}
            {button('Strikethrough — ~text~', 'Strikethrough', <FormatStrikethroughIcon fontSize="small" />, () =>
                apply((t, s, e) => wrapSelection(t, s, e, MRKDWN_MARKER.strike)),
            )}
            {button('Code — `text`', 'Code', <CodeIcon fontSize="small" />, () =>
                apply((t, s, e) => wrapSelection(t, s, e, MRKDWN_MARKER.code)),
            )}
            {button('Bullet the selected lines', 'Bullet', <FormatListBulletedIcon fontSize="small" />, () =>
                apply(bulletLines),
            )}
            {button('Link — <url|text>', 'Link', <LinkIcon fontSize="small" />, () => {
                const el = inputRef.current;
                selection.current = { start: el?.selectionStart ?? 0, end: el?.selectionEnd ?? 0 };
                setLinkOpen(true);
            })}
            <LinkDialog
                open={linkOpen}
                onClose={() => setLinkOpen(false)}
                onInsert={(url) => {
                    const { start, end } = selection.current;
                    apply((t) => insertLink(t, start, end, url));
                    setLinkOpen(false);
                }}
            />
        </Stack>
    );
}

function LinkDialog({
    open,
    onClose,
    onInsert,
}: {
    open: boolean;
    onClose: () => void;
    onInsert: (url: string) => void;
}) {
    const [url, setUrl] = useState('');
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Link the selected text</DialogTitle>
            <DialogContent>
                <TextField
                    label="URL"
                    placeholder="https://finvari.atlassian.net/browse/FE-1234"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    fullWidth
                    autoFocus
                    margin="dense"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={url.trim() === ''} onClick={() => onInsert(url.trim())}>
                    Insert
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Any block type without a form of its own — an image, a context footer — edited as its
 * own JSON rather than the whole file, so the operator still sees one block at a time.
 *
 * This one keeps local text because half-typed JSON does not parse, and writing it into
 * the draft would take the whole editor down to the JSON view. It commits on blur: the
 * committed block comes back re-formatted, and re-formatting under a moving caret is
 * worse than waiting for the operator to finish.
 */
function RawFields({ block, index, json, onChange, disabled }: BlockProps) {
    const incoming = blockToJson(block);
    const [text, setText] = useState(incoming);
    const [error, setError] = useState<string | undefined>();
    const committed = useRef(incoming);

    // Re-seed when this card is showing a different block than it was — a move or a
    // delete above it shifts every block below into a new card.
    useEffect(() => {
        if (incoming === committed.current) return;
        committed.current = incoming;
        setText(incoming);
        setError(undefined);
    }, [incoming]);

    const commit = () => {
        if (text === committed.current) return;
        const parsed = parseBlockJson(text);
        if (!parsed.ok) {
            setError(parsed.error);
            return;
        }
        setError(undefined);
        committed.current = text;
        onChange(setRawBlock(json, index, parsed.block));
    };

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={1}>
                    <TextField
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onBlur={commit}
                        multiline
                        minRows={4}
                        fullWidth
                        disabled={disabled}
                        error={!!error}
                        slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
                    />
                    {error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Edited as JSON — this block type has no form of its own.
                        </Typography>
                    )}
                </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <SlackBlocks blocks={[block]} />
                </Paper>
            </Grid>
        </Grid>
    );
}

function AddBlockMenu({
    label,
    disabled,
    iconOnly,
    onPick,
}: {
    label: string;
    disabled?: boolean;
    iconOnly?: boolean;
    onPick: (kind: AnnouncementBlockKind) => void;
}) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    return (
        <>
            {iconOnly ? (
                <Tooltip title={label}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label={label}
                            disabled={disabled}
                            onClick={(e) => setAnchor(e.currentTarget)}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={disabled}
                    onClick={(e) => setAnchor(e.currentTarget)}
                >
                    {label}
                </Button>
            )}
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {ADDABLE.map((option) => (
                    <MenuItem
                        key={option.kind}
                        onClick={() => {
                            setAnchor(null);
                            onPick(option.kind);
                        }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
